import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import useRevalidateParent from "../../../../../hooks/useRevalidateParent";
import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import LoadingIndicator from "../../../../global/LoadingIndicator";
import ResponseModal from "../../../../global/ResponseModal";
import Accordion from "../../../../ui library/components/accordions/Accordion";
import AccordionList from "../../../../ui library/components/accordions/AccordionList";
import BasicButton from "../../../../ui library/components/buttons/BasicButton";
import AutoCompleteDropdown from "../../../../ui library/components/dropdowns/AutoCompleteDropdown";
import DragAndDropContainer from "../../../../ui library/components/file-upload/DragAndDropContainer";
import {
  formatDateAppointment,
  toValidDate,
  getCenturyDateBounds,
  isWithinDateBounds,
} from "../../../../ui library/helpers/functions";
import { getHiddenDocumentTypeKeys, getSelectableDocTypes } from "../../utils/documentTypeOptions";
import { useDocumentListStore } from "../../ViewDocuments/hooks/useDocumentListStore";
import { useUploadDocumentStore } from "../hooks/useUploadDocumentStore";

import BillAccordionDetails from "./CustomAccordion/BillAccordionDetails";
import BillAccordionSummary from "./CustomAccordion/BillAccordionSummary";
import DocumentUploadHeader from "./DocumentUploadHeader";
import SearchableProviderDropdown from "./SearchableProviderDropdown";

let globalIdCounter = 0;
const generateId = () => ++globalIdCounter;

const DocumentUploadWithApptBills = ({ selectedProviderId, isLaunchedFromAppointmentsTab }) => {
  const fieldOptionRepository = useService("repositories/field-option-repository");
  const documentRepository = useService("repositories/document-repository");
  const documentValidateRepository = useService("repositories/document-validate-repository");
  const providerRepository = useService("repositories/provider-repository");
  const providerTypesRepository = useService("repositories/provider-type-repository");

  const revalidateParent = useRevalidateParent("rvClientDetail");

  const { setServiceLimit, setServiceOffset, setProvider } = useUploadDocumentStore();
  const { id } = useParams();

  const [fileReq, setFileReq] = useState("");
  const [documentsOptions, setDocumentsOptions] = useState(null);
  const [documentValidations, setDocValidations] = useState(null);
  const [isDocSourceRequired, setIsDocSourceRequired] = useState(false);
  const [isRelatedProviderRequired, setIsRelatedProviderRequired] = useState(false);
  const [isReferralFromRequired, setIsReferralFromRequired] = useState(false);
  const [isReferralToRequired, setIsReferralToRequired] = useState(false);
  const [isApptBillCreationAllowed, setIsApptBillCreationAllowed] = useState(true);
  const [maxDocSize, setMaxDocSize] = useState("Max Size: 100MB");
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedDocSource, setSelectedDocSource] = useState(null);
  const [selectedRelatedProvider, setSelectedRelatedProvider] = useState(null);
  const [selectedReferralFrom, setSelectedReferralFrom] = useState(null);
  const [selectedReferralTo, setSelectedReferralTo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initDocType, setInitDocType] = useState(null);
  const [initDocSource, setInitDocSource] = useState("");
  const [initRelatedProvider, setInitRelatedProvider] = useState("");
  const setIsUploadModalOpen = useDocumentListStore((state) => state.setIsUploadModalOpen);
  const [accordionList, setAccordionList] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const defaultSourceIdFromAppointmentsTab = 3;
  const { min: minDate, max: maxDate } = useMemo(() => getCenturyDateBounds(), []);

  useEffect(() => {
    setServiceOffset(0);
    setServiceLimit(25);
    setAccordionList(new Map());
  }, [selectedRelatedProvider]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const requests = [
          fieldOptionRepository.query({
            content_type: "document",
            field_name: "document_type",
            limit: 100,
          }),
          fieldOptionRepository.query({
            content_type: "document",
            field_name: "source",
          }),
          documentValidateRepository.get(),
        ];

        if (isLaunchedFromAppointmentsTab && selectedProviderId) {
          requests.push(providerRepository.getProviders());
        }

        const [docTypesRaw, sourcesRaw, validations, providersData] = await Promise.all(requests);

        const docTypeOptions =
          docTypesRaw?.map((opt) => ({
            id: +opt.key,
            label: opt.label,
            additionalAttributes: opt.additionalAttributes,
          })) || [];
        const selectableDocTypeOptions = getSelectableDocTypes(
          docTypeOptions,
          getHiddenDocumentTypeKeys(docTypesRaw)
        );

        const sourceOptions =
          sourcesRaw?.map((opt) => ({
            id: +opt.key,
            label: opt.label,
          })) || [];

        setDocumentsOptions({ docTypeOptions: selectableDocTypeOptions, sourceOptions });
        setDocValidations(validations);
        setMaxDocSize(validations?.maxFileSize);

        if (isLaunchedFromAppointmentsTab) {
          const selectedDocType = docTypeOptions.find(
            (d) => d.additionalAttributes.isDefault === true
          );
          const selectedDocSource = sourceOptions.find(
            (s) => s.id === defaultSourceIdFromAppointmentsTab
          );
          const mappedProviders =
            providersData?.map((p) => ({
              id: p.key,
              label: p.value,
              providerTypes: p.providerTypes,
            })) || [];
          const selectedRelatedProvider = mappedProviders.find((p) => p.id === selectedProviderId);

          setInitDocType(selectedDocType);
          setInitDocSource(selectedDocSource);
          setInitRelatedProvider(selectedRelatedProvider);

          setSelectedDocType(selectedDocType);
          setSelectedDocSource(selectedDocSource);
          setSelectedRelatedProvider(selectedRelatedProvider);

          docTypeValidate(validations, selectedDocType);
        }
      } catch (error) {
        handleNeedsTypeFail(error);
      }
    };

    fetchOptions();
  }, [fieldOptionRepository, documentValidateRepository, providerRepository, id]);

  const onClearDocType = () => {
    resetAll();
  };

  const resetAll = () => {
    setFileReq("");
    setIsRelatedProviderRequired(false);
    setIsDocSourceRequired(false);
    setIsReferralFromRequired(false);
    setIsReferralToRequired(false);
    setSelectedDocType(initDocType);
    setSelectedDocSource(initDocSource);
    setSelectedRelatedProvider(initRelatedProvider);
    setSelectedReferralFrom("");
    setSelectedReferralTo("");
    setSelectedFile("");
    setAccordionList(new Map());

    if (isLaunchedFromAppointmentsTab) {
      docTypeValidate(documentValidations, initDocType);
    }
  };

  const docTypeValidate = (docValidationList, value) => {
    setFileReq(docValidationList?.validExtensions[value?.id].join(", "));
    setIsDocSourceRequired(docValidationList?.required?.source?.includes(value?.id));
    if (!isLaunchedFromAppointmentsTab) {
      setIsRelatedProviderRequired(docValidationList?.required?.provider?.includes(value?.id));
    } else {
      setIsRelatedProviderRequired(true);
    }
    setIsReferralFromRequired(docValidationList?.required?.referral_from?.includes(value?.id));
    setIsReferralToRequired(docValidationList?.required?.referral_to?.includes(value?.id));
    if (
      value?.additionalAttributes?.canAddAppointments &&
      value?.additionalAttributes?.canAddBills
    ) {
      setIsApptBillCreationAllowed(true);
    } else {
      setIsApptBillCreationAllowed(false);
    }
  };

  const onChangeDocType = (e, value) => {
    setSelectedDocType(value);
    if (!value) {
      onClearDocType();
      return;
    }
    docTypeValidate(documentValidations, value);
  };

  const onChangeDocSource = (e, value) => {
    setSelectedDocSource(value);
  };

  const onChangeRelatedProvider = async (e, value) => {
    try {
      const providerTypeOfSelectedProvider = await providerTypesRepository.query({
        provider: value.id,
      });
      setProviderTypes(providerTypeOfSelectedProvider);
      setSelectedRelatedProvider({
        ...value,
        providerTypes: providerTypeOfSelectedProvider,
      });
      setProvider({
        ...value,
        providerTypes: providerTypeOfSelectedProvider,
      });
    } catch (error) {
      setUploadFailed(true);
      const errArr = ApiRequestErrorHandler(error?.response?.data || error);
      setErrorArray(errArr);
    }
  };

  const onChangeReferralFrom = (e, value) => {
    setSelectedReferralFrom(value);
  };

  const onChangeReferralTo = (e, value) => {
    setSelectedReferralTo(value);
  };

  const onChangeFile = (e, value) => {
    setSelectedFile(value);
  };

  const onDeleteAppointmentRow = (idToDelete) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      newMap.delete(idToDelete);
      return newMap;
    });
  };

  const onDeleteBillRow = (apptId, billIdToDelete) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      newMap.get(apptId).bills.delete(billIdToDelete);
      return newMap;
    });
  };

  const updateAccordionField = (id, fieldName, newValue) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      const entry = newMap.get(id);
      if (entry) {
        if (typeof fieldName === "object") {
          newMap.set(id, { ...entry, ...fieldName });
        } else {
          newMap.set(id, { ...entry, [fieldName]: newValue });
        }
      }
      return newMap;
    });
  };

  const handleUpdateProviderTypeId = (id, newProviderType) => {
    updateAccordionField(id, "provider_type_id", newProviderType?.id || null);
  };

  const handleUpdateAppointmentDate = (id, payload) => {
    const { date, hasError } =
      typeof payload === "object"
        ? { date: payload?.date ?? null, hasError: Boolean(payload?.hasError) }
        : { date: payload, hasError: false };

    if (hasError) {
      updateAccordionField(id, { appointment_date: null, appointment_date_error: true });
      return;
    }

    if (!date) {
      updateAccordionField(id, { appointment_date: null, appointment_date_error: false });
      return;
    }

    const parsedDate = toValidDate(date);
    if (!parsedDate || !isWithinDateBounds(parsedDate, minDate, maxDate)) {
      updateAccordionField(id, {
        appointment_date: null,
        appointment_date_error: true,
      });
      return;
    }

    updateAccordionField(id, {
      appointment_date: formatDateAppointment(parsedDate),
      appointment_date_error: false,
    });
  };

  const handleUpdateBillValues = (apptId, billId, updatedFields) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      const appointment = newMap.get(apptId);
      if (!appointment) {
        return prev;
      }

      const bill = appointment.bills.get(billId);
      if (!bill) {
        return prev;
      }

      appointment.bills.set(billId, { ...bill, ...updatedFields });
      return newMap;
    });
  };

  const handleUpdateBillTotal = (apptId, billId, newTotal) => {
    handleUpdateBillValues(apptId, billId, { price: newTotal });
  };

  const handleUpdateServiceId = (apptId, billId, newServiceId, newServiceInfo) => {
    handleUpdateBillValues(apptId, billId, {
      service_id: newServiceId,
      service_info: newServiceInfo,
    });
  };

  const onCreateAppBtnClick = () => {
    setAccordionList((prev) => {
      const id = generateId();
      const newEntry = {
        id,
        appointment_date: null,
        appointment_date_error: false,
        provider_type_id: null,
        bills: new Map(),
        deleteRow: () => onDeleteAppointmentRow(id),
        updateProviderTypeId: handleUpdateProviderTypeId,
        updateScheduled: handleUpdateAppointmentDate,
        onAddBillBtnClick: onAddBillBtnClick,
      };
      const newMap = new Map(prev);
      newMap.set(id, newEntry);
      return newMap;
    });
  };

  const onAddBillBtnClick = (apptId) => {
    setAccordionList((prev) => {
      const id = generateId();
      const currentAppointment = prev.get(apptId);
      const newEntry = {
        id,
        service_id: "",
        service_info: null,
        price: null,
        deleteRow: () => onDeleteBillRow(apptId, id),
        updateServiceId: handleUpdateServiceId,
        updatePrice: handleUpdateBillTotal,
      };
      const newMap = new Map(prev);
      currentAppointment.bills.set(id, newEntry);
      newMap.set(apptId, currentAppointment);
      return newMap;
    });
  };

  const onUploadBtnClick = async () => {
    try {
      setLoading(true);

      const postData = {
        case: id,
        document_type: selectedDocType?.id,
        file: selectedFile,
        ...(isDocSourceRequired || selectedDocSource?.id ? { source: selectedDocSource?.id } : {}),
        ...(isRelatedProviderRequired && selectedRelatedProvider?.id
          ? { provider: selectedRelatedProvider.id }
          : {}),
        ...(isLaunchedFromAppointmentsTab || (isReferralFromRequired && selectedReferralFrom?.id)
          ? { referral_from: selectedReferralFrom?.id }
          : {}),
        ...(isReferralToRequired && selectedReferralTo?.id
          ? { referral_to: selectedReferralTo.id }
          : {}),
      };

      if (accordionList && accordionList.size > 0) {
        const appointmentsArray = Array.from(accordionList.values());

        const hasInvalidDate = appointmentsArray.some((appointment) => {
          if (appointment?.appointment_date_error) {
            return true;
          }

          if (!appointment?.appointment_date) {
            return false;
          }

          const parsed = toValidDate(appointment.appointment_date);
          return !parsed || !isWithinDateBounds(parsed, minDate, maxDate);
        });

        if (hasInvalidDate) {
          setLoading(false);
          setErrorArray(["Please correct appointment dates before uploading documents."]);
          setUploadFailed(true);
          return;
        }

        postData.appointments = appointmentsArray.map((appointment) => {
          const parsed = appointment.appointment_date
            ? toValidDate(appointment.appointment_date)
            : null;
          const appointmentDate =
            parsed && isWithinDateBounds(parsed, minDate, maxDate)
              ? formatDateAppointment(parsed)
              : formatDateAppointment(new Date());
          return {
            appointment_date: appointmentDate,
            provider_type_id: appointment?.provider_type_id,
            ...(appointment?.bills?.size > 0 && {
              bills: Array.from(appointment.bills.values()).map(({ service_id, price }) => ({
                service_id,
                price,
              })),
            }),
          };
        });
      }

      const res = await documentRepository.upload(
        postData,
        { urlPath: "documents/upload_with_appointments_and_bills/" },
        "POST"
      );
      setUploadSuccess(true);
      return res;
    } catch (error) {
      handleNeedsTypeFail(error);
    } finally {
      setLoading(false);
      revalidateParent();
    }
  };

  const handleUploadSuccess = () => {
    setUploadSuccess(false);
    resetAll();
    setIsUploadModalOpen(false);
  };
  const handleNeedsTypeFail = (error) => {
    setUploadFailed(true);
    const errArr = ApiRequestErrorHandler(error?.response?.data || error);
    setErrorArray(errArr);
  };

  return (
    <div className="h-full">
      <DocumentUploadHeader maxDocSize={maxDocSize} fileReq={fileReq} />

      <div className="py-2">
        <div className="mx-auto my-8 w-full" data-testid="container-docTypes">
          <AutoCompleteDropdown
            initValue={selectedDocType}
            label="Document Types"
            data={documentsOptions?.docTypeOptions || []}
            onValueChange={(e, value) => {
              onChangeDocType(e, value);
            }}
          />
        </div>

        <div className="mx-auto my-8 w-full" data-testid="container-docSource">
          <AutoCompleteDropdown
            initValue={selectedDocSource}
            label={"Document Source" + (isDocSourceRequired ? "*" : "")}
            data={documentsOptions?.sourceOptions || []}
            onValueChange={onChangeDocSource}
            addNoSelectionOption={!isDocSourceRequired}
          />
        </div>

        {isRelatedProviderRequired && (
          <div className="mx-auto my-8 w-full" data-testid="container-relatedProvider">
            <SearchableProviderDropdown
              initValue={selectedRelatedProvider}
              label="Related Provider"
              repository={providerRepository}
              mode="relatedProviders"
              caseId={id}
              onChange={(value) => onChangeRelatedProvider(null, value)}
              disabled={isLaunchedFromAppointmentsTab}
              required
            />
          </div>
        )}

        {(isReferralFromRequired || isReferralToRequired) && (
          <div className="mx-4 mb-10 mt-1 rounded-sm bg-yellow-100 p-4 text-sm text-black">
            <strong>NOTE: </strong>Only providers that are already assigned to the client's case can
            be chosen below. If the provider needed is not shown below, please add them to the case
            before uploading the referral file.
          </div>
        )}

        {isReferralFromRequired && (
          <div className="mx-auto my-8 w-full" data-testid="container-referralFrom">
            <SearchableProviderDropdown
              initValue={selectedReferralFrom}
              label="Referral From"
              repository={providerRepository}
              mode="relatedProviders"
              caseId={id}
              onChange={(value) => onChangeReferralFrom(null, value)}
              required
            />
          </div>
        )}

        {isReferralToRequired && (
          <div className="mx-auto my-8 w-full" data-testid="container-referralTo">
            <SearchableProviderDropdown
              initValue={selectedReferralTo}
              label="Referral To"
              repository={providerRepository}
              mode="providers"
              onChange={(value) => onChangeReferralTo(null, value)}
              required
            />
          </div>
        )}

        {isApptBillCreationAllowed && selectedRelatedProvider?.id !== undefined && (
          <div className="flex flex-col" key={accordionList}>
            {accordionList.size > 0 && (
              <fieldset
                component="fieldset"
                className="group mb-4 rounded-md border-[1px] border-gray-300 px-4 py-2 hover:border-gray-700"
              >
                <legend className="p-1 text-xs text-gray-500">Appointments</legend>
                <AccordionList
                  accordionListData={accordionList}
                  accordionVariant="default"
                  summaryContent={BillAccordionSummary}
                  expandedContent={<BillAccordionDetails />}
                  expandIcon={<ExpandMore className="text-gray-500" />}
                  shrinkIcon={<ExpandLess className="text-gray-500" />}
                  expandOnIconClickOnly={true}
                >
                  <Accordion />
                </AccordionList>
              </fieldset>
            )}
            <div className="w-100 flex flex-row flex-row-reverse">
              <BasicButton
                text="Create Appointment"
                className={`w-full items-center self-center bg-green-600 px-4 py-2 text-lg hover:bg-green-700`}
                icon={<AddCircleOutlineRoundedIcon />}
                onClick={onCreateAppBtnClick}
              />
            </div>
          </div>
        )}

        <div className="mx-auto my-8 w-full" data-testid="container-dragAndDropBox">
          <DragAndDropContainer initValue={selectedFile} onValueChange={onChangeFile} />
        </div>

        <div
          className="mx-auto my-8 flex w-full flex-row justify-evenly gap-4 text-center"
          data-testid="container-uploadButton"
        >
          <BasicButton
            text="Upload Document"
            className="w-1/2 items-center self-center bg-blue-600 px-4 py-2 text-lg hover:bg-blue-700"
            onClick={() => {
              onUploadBtnClick();
            }}
          />
          <BasicButton
            variant="outlined"
            text="Clear"
            className="w-1/2 items-center self-center px-4 py-2 text-lg text-blue-600 hover:bg-blue-100"
            onClick={resetAll}
          />
        </div>
      </div>
      <LoadingIndicator isLoading={loading} />
      <ResponseModal
        title="Update Success"
        description="The image/document was successfully updated"
        openBool={uploadSuccess}
        setOpenBool={setUploadSuccess}
        handleCloseFunc={handleUploadSuccess}
      />
      <ResponseModal
        title="Update failed"
        isError={true}
        openBool={uploadFailed}
        setOpenBool={setUploadFailed}
        errorMessage={errorArray}
      />
    </div>
  );
};

export default DocumentUploadWithApptBills;
